import React, { useState, useEffect } from 'react';

// ============================================================================
// SANDBOXED DEMO PAGE — No real API calls, all fake data
// Route: /demo
// Replace old demo links on LandingPage.jsx with /demo
// ============================================================================

const INDUSTRIES = {
  spa: {
    key: 'spa',
    label: 'Spa & Wellness',
    icon: '🧖‍♀️',
    businessName: 'Serenity Day Spa',
    tagline: 'Luxury treatments in the heart of Las Vegas',
    bg: '#faf6ef',
    accent: '#8B6F4E',
    accentLight: '#C4A97D',
    headerFont: "'Cormorant Garamond', serif",
    bodyFont: "'Jost', sans-serif",
    dark: false,
    services: [
      { id: 1, name: 'Swedish Massage', duration: 60, price: 120, category: 'Massage' },
      { id: 2, name: 'Deep Tissue Massage', duration: 90, price: 160, category: 'Massage' },
      { id: 3, name: 'Hydrating Facial', duration: 45, price: 95, category: 'Facial' },
      { id: 4, name: 'Hot Stone Therapy', duration: 75, price: 145, category: 'Massage' },
      { id: 5, name: 'Couples Retreat Package', duration: 120, price: 280, category: 'Package' },
      { id: 6, name: 'Aromatherapy Session', duration: 60, price: 110, category: 'Wellness' },
    ],
    staff: [
      { id: 1, name: 'Maria Santos', role: 'Lead Therapist', avatar: '👩‍⚕️' },
      { id: 2, name: 'Jessica Kim', role: 'Esthetician', avatar: '👩' },
      { id: 3, name: 'David Chen', role: 'Massage Therapist', avatar: '👨‍⚕️' },
    ],
    clients: [
      { name: 'Sarah Mitchell', visits: 12, spent: 1840, status: 'VIP' },
      { name: 'Jennifer Wu', visits: 8, spent: 1120, status: 'Regular' },
      { name: 'Amanda Torres', visits: 5, spent: 680, status: 'Regular' },
      { name: 'Rachel Green', visits: 15, spent: 2200, status: 'VIP' },
    ],
    stats: { revenue: 14280, bookings: 87, clients: 42, rating: 4.9 },
  },
  barbershop: {
    key: 'barbershop',
    label: 'Barbershop',
    icon: '💈',
    businessName: 'Kings Barbershop',
    tagline: 'Premium cuts. Clean fades. Walk-ins welcome.',
    bg: '#0a0a0a',
    accent: '#D4A843',
    accentLight: '#E8C76A',
    headerFont: "'Bebas Neue', sans-serif",
    bodyFont: "'Inter', sans-serif",
    dark: true,
    services: [
      { id: 1, name: 'Classic Haircut', duration: 30, price: 35, category: 'Cuts' },
      { id: 2, name: 'Skin Fade', duration: 45, price: 45, category: 'Cuts' },
      { id: 3, name: 'Beard Trim & Shape', duration: 20, price: 20, category: 'Beard' },
      { id: 4, name: 'Hot Towel Shave', duration: 30, price: 30, category: 'Shave' },
      { id: 5, name: 'Haircut + Beard Combo', duration: 50, price: 55, category: 'Combo' },
      { id: 6, name: 'Kids Haircut', duration: 20, price: 25, category: 'Cuts' },
    ],
    staff: [
      { id: 1, name: 'Marcus Johnson', role: 'Master Barber', avatar: '💇‍♂️' },
      { id: 2, name: 'Tony Rivera', role: 'Senior Barber', avatar: '✂️' },
      { id: 3, name: 'Darius Williams', role: 'Barber', avatar: '💈' },
      { id: 4, name: 'Chris Park', role: 'Junior Barber', avatar: '👨' },
    ],
    clients: [
      { name: 'James Cooper', visits: 24, spent: 960, status: 'VIP' },
      { name: 'Mike Rodriguez', visits: 18, spent: 720, status: 'Regular' },
      { name: 'Andre Washington', visits: 10, spent: 450, status: 'Regular' },
      { name: 'Tyler Kim', visits: 30, spent: 1350, status: 'VIP' },
    ],
    stats: { revenue: 9850, bookings: 215, clients: 78, rating: 4.8 },
  },
  auto: {
    key: 'auto',
    label: 'Auto / Moto',
    icon: '🏍️',
    businessName: 'Apex Moto Works',
    tagline: 'Expert motorcycle service & repair',
    bg: '#111116',
    accent: '#E63946',
    accentLight: '#FF6B6B',
    headerFont: "'Oswald', sans-serif",
    bodyFont: "'DM Sans', sans-serif",
    dark: true,
    services: [
      { id: 1, name: 'Oil Change', duration: 30, price: 75, category: 'Maintenance' },
      { id: 2, name: 'Tire Change (pair)', duration: 60, price: 150, category: 'Tires' },
      { id: 3, name: 'Full Inspection', duration: 45, price: 95, category: 'Diagnostic' },
      { id: 4, name: 'Brake Service', duration: 90, price: 220, category: 'Brakes' },
      { id: 5, name: 'Chain & Sprocket', duration: 60, price: 180, category: 'Drivetrain' },
      { id: 6, name: 'Custom Exhaust Install', duration: 120, price: 350, category: 'Custom' },
    ],
    staff: [
      { id: 1, name: 'Jake Morrison', role: 'Lead Mechanic', avatar: '🔧' },
      { id: 2, name: 'Carlos Reyes', role: 'Technician', avatar: '👨‍🔧' },
      { id: 3, name: 'Sam Tucker', role: 'Custom Specialist', avatar: '🏍️' },
    ],
    clients: [
      { name: 'Ryan Peters', visits: 6, spent: 1420, status: 'Regular' },
      { name: 'Kevin Nakamura', visits: 9, spent: 2180, status: 'VIP' },
      { name: 'Brandon Lee', visits: 4, spent: 680, status: 'Regular' },
      { name: 'Derek Stone', visits: 12, spent: 3200, status: 'VIP' },
    ],
    stats: { revenue: 18500, bookings: 52, clients: 31, rating: 4.9 },
  },
  smoke: {
    key: 'smoke',
    label: 'Smoke Shop',
    icon: '🌿',
    businessName: 'Cloud 9 Smoke & Vape',
    tagline: 'Premium smoke & vape products',
    bg: '#0D1117',
    accent: '#7C3AED',
    accentLight: '#A78BFA',
    headerFont: "'Space Grotesk', sans-serif",
    bodyFont: "'DM Sans', sans-serif",
    dark: true,
    services: [], // Smoke shops use POS, not bookings
    inventory: [
      { name: 'RAW King Size Cones (32pk)', price: 12.99, stock: 45, category: 'Papers' },
      { name: 'Puffco Peak Pro', price: 249.99, stock: 3, category: 'Devices' },
      { name: 'Elf Bar BC5000', price: 15.99, stock: 120, category: 'Disposables' },
      { name: 'GRAV Helix Beaker', price: 89.99, stock: 8, category: 'Glass' },
      { name: 'Clipper Lighters (48ct)', price: 42.00, stock: 22, category: 'Accessories' },
      { name: 'Lost Mary MO5000', price: 14.99, stock: 85, category: 'Disposables' },
    ],
    clients: [
      { name: 'DeShawn Brooks', visits: 34, spent: 890, status: 'VIP' },
      { name: 'Jordan Taylor', visits: 18, spent: 420, status: 'Regular' },
      { name: 'Marcus Williams', visits: 22, spent: 650, status: 'VIP' },
      { name: 'Alex Nguyen', visits: 8, spent: 210, status: 'Regular' },
    ],
    stats: { revenue: 22300, bookings: 0, clients: 156, rating: 4.7 },
  },
  rental: {
    key: 'rental',
    label: 'Equipment Rental',
    icon: '🏗️',
    businessName: 'On The Grind Equipment Rentals',
    tagline: 'Concrete coating equipment for contractors — Las Vegas',
    bg: '#1e2235',
    accent: '#f97316',
    accentLight: '#fb923c',
    headerFont: "'DM Mono', monospace",
    bodyFont: "'DM Sans', sans-serif",
    dark: true,
    services: [],
    equipment: [
      { name: 'Concrete Grinder 10"', category: 'Grinding', priceDay: 150, deposit: 500, available: 3 },
      { name: 'Planetary Grinder 32"', category: 'Grinding', priceDay: 450, deposit: 1500, available: 1 },
      { name: 'Shot Blaster', category: 'Blasting', priceDay: 400, deposit: 1500, available: 1 },
      { name: 'Scarifier', category: 'Scarifying', priceDay: 350, deposit: 1200, available: 2 },
      { name: 'Epoxy Sprayer', category: 'Application', priceDay: 120, deposit: 400, available: 4 },
      { name: 'Dust Collector 3HP', category: 'Dust Control', priceDay: 140, deposit: 450, available: 2 },
    ],
    orders: [
      { client: 'Bulletproof Synthetic Coatings', status: 'Returned', payment: 'Paid', total: 373.64, from: 'Feb 23', to: 'Feb 27' },
      { client: 'Rose Concrete Coatings', status: 'Returned', payment: 'Paid', total: 4557.25, from: 'Feb 5', to: 'Feb 5' },
      { client: 'Fav Tech', status: 'Returned', payment: 'Payment Due', total: 1725.62, from: 'Feb 27', to: 'Mar 2' },
      { client: 'Advanced Concrete And Deck', status: 'Reserved', payment: 'Payment Due', total: 1200.00, from: 'Mar 10', to: 'Mar 14' },
      { client: 'Richard Roper', status: 'Started', payment: 'Partial', total: 79.84, from: 'Mar 5', to: 'Mar 8' },
    ],
    clients: [
      { name: 'Bulletproof Synthetic Coatings', visits: 12, spent: 4820, status: 'VIP' },
      { name: 'Rose Concrete Coatings', visits: 8, spent: 9114, status: 'VIP' },
      { name: 'Fav Tech', visits: 5, spent: 2190, status: 'Regular' },
      { name: 'Ryan Thomas Construction', visits: 9, spent: 3100, status: 'Regular' },
    ],
    stats: { revenue: 51738, bookings: 107, clients: 65, rating: 4.9 },
  },
};

// Fake time slots
const TIME_SLOTS = [
  '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
  '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM',
  '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM', '5:00 PM',
];

// ============================================================================
// MINI DASHBOARD PREVIEW
// ============================================================================
function DashboardPreview({ industry }) {
  const d = INDUSTRIES[industry];
  const textColor = d.dark ? '#fff' : '#252840';
  const textMuted = d.dark ? 'rgba(255,255,255,0.5)' : '#888';
  const cardBg = d.dark ? 'rgba(255,255,255,0.06)' : '#fff';
  const cardBorder = d.dark ? 'rgba(255,255,255,0.08)' : '#eee';

  return (
    <div style={{
      background: d.bg, borderRadius: 16, overflow: 'hidden',
      border: `1px solid ${cardBorder}`, height: '100%', minHeight: 480,
    }}>
      {/* Top bar */}
      <div style={{
        padding: '14px 20px', borderBottom: `1px solid ${cardBorder}`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <div>
          <div style={{ fontFamily: d.headerFont, fontSize: 18, fontWeight: 700, color: textColor }}>
            {d.businessName}
          </div>
          <div style={{ fontSize: 11, color: textMuted }}>Owner Dashboard</div>
        </div>
        <div style={{
          width: 32, height: 32, borderRadius: '50%', background: d.accent,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 14, color: '#fff', fontWeight: 700,
        }}>
          {d.businessName[0]}
        </div>
      </div>

      {/* Stats row */}
      <div style={{ padding: '16px 20px', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
        {[
          { label: 'Revenue', value: `$${d.stats.revenue.toLocaleString()}`, icon: '💰' },
          { label: industry === 'smoke' ? 'Transactions' : industry === 'rental' ? 'Orders' : 'Bookings', value: industry === 'smoke' ? '348' : d.stats.bookings, icon: '📊' },
          { label: 'Clients', value: d.stats.clients, icon: '👥' },
          { label: 'Rating', value: `${d.stats.rating} ★`, icon: '⭐' },
        ].map((stat, i) => (
          <div key={i} style={{
            background: cardBg, borderRadius: 10, padding: '12px 14px',
            border: `1px solid ${cardBorder}`,
          }}>
            <div style={{ fontSize: 11, color: textMuted, marginBottom: 4 }}>{stat.icon} {stat.label}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: textColor, fontFamily: d.headerFont }}>
              {stat.value}
            </div>
          </div>
        ))}
      </div>

      {/* Recent activity */}
      <div style={{ padding: '0 20px 16px' }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: textColor, marginBottom: 10 }}>
          Recent {industry === 'smoke' ? 'Sales' : industry === 'rental' ? 'Orders' : 'Bookings'}
        </div>
        {(industry === 'smoke' ? [
          { text: 'DeShawn Brooks — Elf Bar BC5000 × 2', time: '2m ago', amount: '$31.98' },
          { text: 'Jordan Taylor — RAW Cones + Clipper', time: '18m ago', amount: '$54.99' },
          { text: 'Walk-in — Puffco Peak Pro', time: '1h ago', amount: '$249.99' },
        ] : industry === 'rental' ? [
          { text: `${d.orders[0].client} — Returned`, time: 'Feb 27', amount: `$${d.orders[0].total}` },
          { text: `${d.orders[1].client} — Returned`, time: 'Feb 5', amount: `$${d.orders[1].total}` },
          { text: `${d.orders[3].client} — Reserved`, time: 'Mar 10', amount: `$${d.orders[3].total}` },
        ] : [
          { text: `${d.clients[0].name} — ${d.services[0]?.name}`, time: '2m ago', amount: `$${d.services[0]?.price}` },
          { text: `${d.clients[1].name} — ${d.services[1]?.name}`, time: '35m ago', amount: `$${d.services[1]?.price}` },
          { text: `${d.clients[2].name} — ${d.services[2]?.name}`, time: '1h ago', amount: `$${d.services[2]?.price}` },
        ]).map((item, i) => (
          <div key={i} style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '10px 12px', background: cardBg, borderRadius: 8,
            marginBottom: 6, border: `1px solid ${cardBorder}`,
          }}>
            <div>
              <div style={{ fontSize: 13, color: textColor, fontWeight: 500 }}>{item.text}</div>
              <div style={{ fontSize: 11, color: textMuted }}>{item.time}</div>
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: d.accent }}>{item.amount}</div>
          </div>
        ))}
      </div>

      {/* Top clients */}
      <div style={{ padding: '0 20px 20px' }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: textColor, marginBottom: 10 }}>Top Clients</div>
        <div style={{ display: 'flex', gap: 8 }}>
          {d.clients.slice(0, 3).map((c, i) => (
            <div key={i} style={{
              flex: 1, background: cardBg, borderRadius: 8, padding: '10px 8px',
              border: `1px solid ${cardBorder}`, textAlign: 'center',
            }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: textColor, marginBottom: 2 }}>
                {c.name.split(' ')[0]}
              </div>
              <div style={{ fontSize: 11, color: textMuted }}>{c.visits} visits</div>
              {c.status === 'VIP' && (
                <div style={{
                  fontSize: 9, fontWeight: 700, color: d.accent, marginTop: 4,
                  background: d.dark ? 'rgba(255,255,255,0.05)' : `${d.accent}15`,
                  borderRadius: 4, padding: '2px 6px', display: 'inline-block',
                }}>VIP</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// BOOKING FLOW PREVIEW (Interactive fake booking)
// ============================================================================
function BookingPreview({ industry }) {
  const d = INDUSTRIES[industry];
  const [step, setStep] = useState(1);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);

  // Reset when industry changes
  useEffect(() => {
    setStep(1);
    setSelectedService(null);
    setSelectedStaff(null);
    setSelectedTime(null);
  }, [industry]);

  const textColor = d.dark ? '#fff' : '#252840';
  const textMuted = d.dark ? 'rgba(255,255,255,0.5)' : '#888';
  const cardBg = d.dark ? 'rgba(255,255,255,0.06)' : '#fff';
  const cardBorder = d.dark ? 'rgba(255,255,255,0.08)' : '#eee';
  const inputBg = d.dark ? 'rgba(255,255,255,0.08)' : '#f8f8fc';

  // Smoke shops don't have bookings — show POS preview instead
  if (industry === 'smoke') {
    return <POSPreview industry={industry} />;
  }
  // Rental shows order preview instead of booking flow
  if (industry === 'rental') {
    return <RentalOrderPreview industry={industry} />;
  }

  return (
    <div style={{
      background: d.bg, borderRadius: 16, overflow: 'hidden',
      border: `1px solid ${cardBorder}`, height: '100%', minHeight: 480,
    }}>
      {/* Header */}
      <div style={{
        background: d.dark
          ? `linear-gradient(135deg, ${d.accent}30, ${d.bg})`
          : `linear-gradient(135deg, ${d.accent}20, ${d.bg})`,
        padding: '20px 24px', textAlign: 'center',
        borderBottom: `1px solid ${cardBorder}`,
      }}>
        <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: 2, color: d.accent, marginBottom: 4, textTransform: 'uppercase' }}>
          Book Online
        </div>
        <div style={{ fontFamily: d.headerFont, fontSize: 22, fontWeight: 700, color: textColor }}>
          {d.businessName}
        </div>
        <div style={{ fontSize: 12, color: textMuted, marginTop: 2 }}>{d.tagline}</div>
      </div>

      {/* Progress bar */}
      <div style={{
        padding: '12px 24px', borderBottom: `1px solid ${cardBorder}`,
        display: 'flex', justifyContent: 'center', gap: 6, alignItems: 'center',
      }}>
        {['Service', 'Provider', 'Time', 'Confirm'].map((s, i) => (
          <React.Fragment key={i}>
            <div style={{
              width: 28, height: 28, borderRadius: '50%', display: 'flex',
              alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700,
              background: step > i + 1 ? d.accent : step === i + 1 ? d.accent : (d.dark ? 'rgba(255,255,255,0.1)' : '#e8e8f0'),
              color: step >= i + 1 ? '#fff' : textMuted, transition: 'all 0.3s',
            }}>
              {step > i + 1 ? '✓' : i + 1}
            </div>
            {i < 3 && (
              <div style={{
                width: 24, height: 2, borderRadius: 1,
                background: step > i + 1 ? d.accent : (d.dark ? 'rgba(255,255,255,0.1)' : '#e8e8f0'),
                transition: 'all 0.3s',
              }} />
            )}
          </React.Fragment>
        ))}
      </div>

      <div style={{ padding: 20 }}>
        {/* Step 1: Select Service */}
        {step === 1 && (
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: textColor, marginBottom: 4, fontFamily: d.headerFont }}>
              Choose a Service
            </div>
            <div style={{ fontSize: 13, color: textMuted, marginBottom: 16 }}>Select what you'd like to book</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {d.services.map(svc => (
                <div
                  key={svc.id}
                  onClick={() => { setSelectedService(svc); setStep(2); }}
                  style={{
                    padding: '14px 16px', borderRadius: 10, cursor: 'pointer',
                    background: selectedService?.id === svc.id ? `${d.accent}15` : cardBg,
                    border: `1px solid ${selectedService?.id === svc.id ? d.accent : cardBorder}`,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    transition: 'all 0.2s',
                  }}
                >
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: textColor }}>{svc.name}</div>
                    <div style={{ fontSize: 12, color: textMuted }}>{svc.duration} min · {svc.category}</div>
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: d.accent }}>${svc.price}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Select Staff */}
        {step === 2 && (
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: textColor, marginBottom: 4, fontFamily: d.headerFont }}>
              Choose Your {industry === 'barbershop' ? 'Barber' : industry === 'auto' ? 'Technician' : 'Provider'}
            </div>
            <div style={{ fontSize: 13, color: textMuted, marginBottom: 16 }}>
              For: {selectedService?.name} · ${selectedService?.price}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div
                onClick={() => { setSelectedStaff({ id: 0, name: 'Any Available', role: '', avatar: '🔄' }); setStep(3); }}
                style={{
                  padding: '14px 16px', borderRadius: 10, cursor: 'pointer',
                  background: cardBg, border: `1px solid ${cardBorder}`,
                  display: 'flex', alignItems: 'center', gap: 12, transition: 'all 0.2s',
                }}
              >
                <div style={{ fontSize: 20 }}>🔄</div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: textColor }}>Any Available</div>
                  <div style={{ fontSize: 12, color: textMuted }}>First available provider</div>
                </div>
              </div>
              {d.staff.map(s => (
                <div
                  key={s.id}
                  onClick={() => { setSelectedStaff(s); setStep(3); }}
                  style={{
                    padding: '14px 16px', borderRadius: 10, cursor: 'pointer',
                    background: cardBg, border: `1px solid ${cardBorder}`,
                    display: 'flex', alignItems: 'center', gap: 12, transition: 'all 0.2s',
                  }}
                >
                  <div style={{ fontSize: 20 }}>{s.avatar}</div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: textColor }}>{s.name}</div>
                    <div style={{ fontSize: 12, color: textMuted }}>{s.role}</div>
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={() => setStep(1)}
              style={{
                marginTop: 12, padding: '8px 16px', border: 'none', borderRadius: 8,
                background: 'transparent', color: textMuted, fontSize: 13, cursor: 'pointer',
              }}
            >
              ← Back
            </button>
          </div>
        )}

        {/* Step 3: Select Time */}
        {step === 3 && (
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: textColor, marginBottom: 4, fontFamily: d.headerFont }}>
              Pick a Time
            </div>
            <div style={{ fontSize: 13, color: textMuted, marginBottom: 16 }}>
              {selectedService?.name} with {selectedStaff?.name}
            </div>
            <div style={{
              fontSize: 13, fontWeight: 600, color: textColor, marginBottom: 10,
              padding: '8px 12px', background: cardBg, borderRadius: 8,
              border: `1px solid ${cardBorder}`, textAlign: 'center',
            }}>
              📅 Today — {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
              {TIME_SLOTS.map((t, i) => {
                // Mark some as unavailable randomly (but deterministically)
                const unavailable = [2, 5, 8, 11].includes(i);
                return (
                  <div
                    key={t}
                    onClick={() => { if (!unavailable) { setSelectedTime(t); setStep(4); } }}
                    style={{
                      padding: '10px 8px', borderRadius: 8, textAlign: 'center',
                      fontSize: 13, fontWeight: 600, cursor: unavailable ? 'not-allowed' : 'pointer',
                      background: selectedTime === t ? d.accent : unavailable ? (d.dark ? 'rgba(255,255,255,0.03)' : '#f5f5f5') : cardBg,
                      color: selectedTime === t ? '#fff' : unavailable ? (d.dark ? 'rgba(255,255,255,0.2)' : '#ccc') : textColor,
                      border: `1px solid ${selectedTime === t ? d.accent : cardBorder}`,
                      opacity: unavailable ? 0.5 : 1,
                      textDecoration: unavailable ? 'line-through' : 'none',
                      transition: 'all 0.2s',
                    }}
                  >
                    {t}
                  </div>
                );
              })}
            </div>
            <button
              onClick={() => setStep(2)}
              style={{
                marginTop: 12, padding: '8px 16px', border: 'none', borderRadius: 8,
                background: 'transparent', color: textMuted, fontSize: 13, cursor: 'pointer',
              }}
            >
              ← Back
            </button>
          </div>
        )}

        {/* Step 4: Confirmation */}
        {step === 4 && (
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: textColor, marginBottom: 4, fontFamily: d.headerFont }}>
              Confirm Your Booking
            </div>
            <div style={{ fontSize: 13, color: textMuted, marginBottom: 16 }}>Review your details below</div>

            {/* Summary card */}
            <div style={{
              background: cardBg, borderRadius: 12, padding: 16,
              border: `1px solid ${cardBorder}`, marginBottom: 16,
            }}>
              {[
                { label: 'Service', value: selectedService?.name },
                { label: 'Provider', value: selectedStaff?.name },
                { label: 'Date', value: new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) },
                { label: 'Time', value: selectedTime },
                { label: 'Duration', value: `${selectedService?.duration} min` },
              ].map((row, i) => (
                <div key={i} style={{
                  display: 'flex', justifyContent: 'space-between', padding: '8px 0',
                  borderBottom: i < 4 ? `1px solid ${cardBorder}` : 'none',
                }}>
                  <span style={{ fontSize: 13, color: textMuted }}>{row.label}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: textColor }}>{row.value}</span>
                </div>
              ))}
              <div style={{
                display: 'flex', justifyContent: 'space-between', padding: '12px 0 0',
                marginTop: 4, borderTop: `2px solid ${d.accent}30`,
              }}>
                <span style={{ fontSize: 15, fontWeight: 700, color: textColor }}>Total</span>
                <span style={{ fontSize: 18, fontWeight: 700, color: d.accent }}>${selectedService?.price}</span>
              </div>
            </div>

            {/* Fake form fields */}
            <div style={{ marginBottom: 12 }}>
              <input
                placeholder="Your Name"
                readOnly
                style={{
                  width: '100%', padding: '12px 14px', borderRadius: 8, border: `1px solid ${cardBorder}`,
                  background: inputBg, color: textColor, fontSize: 14, marginBottom: 8,
                  fontFamily: d.bodyFont, boxSizing: 'border-box',
                }}
              />
              <input
                placeholder="Phone Number"
                readOnly
                style={{
                  width: '100%', padding: '12px 14px', borderRadius: 8, border: `1px solid ${cardBorder}`,
                  background: inputBg, color: textColor, fontSize: 14, marginBottom: 8,
                  fontFamily: d.bodyFont, boxSizing: 'border-box',
                }}
              />
              <input
                placeholder="Email (optional)"
                readOnly
                style={{
                  width: '100%', padding: '12px 14px', borderRadius: 8, border: `1px solid ${cardBorder}`,
                  background: inputBg, color: textColor, fontSize: 14,
                  fontFamily: d.bodyFont, boxSizing: 'border-box',
                }}
              />
            </div>

            {/* SMS Consent - Required for Twilio A2P compliance */}
            <div style={{ display:'flex', alignItems:'flex-start', gap:10, margin:'12px 0', padding:'12px 14px', borderRadius:8, background:'rgba(0,0,0,0.15)', border:`1px solid ${cardBorder}` }}>
              <input
                type="checkbox"
                id="sms-consent"
                defaultChecked
                style={{ marginTop:2, accentColor: d.accent, width:16, height:16, flexShrink:0, cursor:'pointer' }}
              />
              <label htmlFor="sms-consent" style={{ fontSize:12, color:textMuted, lineHeight:1.5, cursor:'pointer' }}>
                I agree to receive appointment reminders, booking confirmations, and service updates via SMS from this business. Message & data rates may apply. Reply <strong>STOP</strong> to opt out at any time. View our <a href="/privacy" target="_blank" style={{color:d.accent}}>Privacy Policy</a> and <a href="/terms" target="_blank" style={{color:d.accent}}>Terms</a>.
              </label>
            </div>
            <button
              onClick={() => setStep(5)}
              style={{
                width: '100%', padding: '14px 0', borderRadius: 10, border: 'none',
                background: d.accent, color: '#fff', fontSize: 15, fontWeight: 700,
                cursor: 'pointer', fontFamily: d.headerFont, letterSpacing: 0.5,
              }}
            >
              Confirm Booking
            </button>
            <button
              onClick={() => setStep(3)}
              style={{
                width: '100%', marginTop: 8, padding: '8px 0', border: 'none', borderRadius: 8,
                background: 'transparent', color: textMuted, fontSize: 13, cursor: 'pointer',
              }}
            >
              ← Back
            </button>
          </div>
        )}

        {/* Step 5: Success */}
        {step === 5 && (
          <div style={{ textAlign: 'center', padding: '30px 0' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
            <div style={{ fontFamily: d.headerFont, fontSize: 22, fontWeight: 700, color: textColor, marginBottom: 4 }}>
              Booking Confirmed!
            </div>
            <div style={{ fontSize: 14, color: textMuted, marginBottom: 20, lineHeight: 1.5 }}>
              {selectedService?.name} with {selectedStaff?.name}<br />
              {selectedTime} · ${selectedService?.price}
            </div>
            <div style={{
              background: `${d.accent}15`, borderRadius: 10, padding: 14,
              fontSize: 13, color: d.accent, fontWeight: 500, marginBottom: 20,
            }}>
              📱 Confirmation SMS sent to customer
            </div>
            <button
              onClick={() => { setStep(1); setSelectedService(null); setSelectedStaff(null); setSelectedTime(null); }}
              style={{
                padding: '10px 24px', borderRadius: 8, border: `1px solid ${d.accent}`,
                background: 'transparent', color: d.accent, fontSize: 13, fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Book Another
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// POS PREVIEW (for Smoke Shops)
// ============================================================================
function POSPreview({ industry }) {
  const d = INDUSTRIES[industry];
  const [cart, setCart] = useState([]);
  const textColor = '#fff';
  const textMuted = 'rgba(255,255,255,0.5)';
  const cardBg = 'rgba(255,255,255,0.06)';
  const cardBorder = 'rgba(255,255,255,0.08)';

  const addToCart = (item) => {
    const existing = cart.find(c => c.name === item.name);
    if (existing) {
      setCart(cart.map(c => c.name === item.name ? { ...c, qty: c.qty + 1 } : c));
    } else {
      setCart([...cart, { ...item, qty: 1 }]);
    }
  };

  const total = cart.reduce((sum, item) => sum + (item.price * item.qty), 0);

  return (
    <div style={{
      background: d.bg, borderRadius: 16, overflow: 'hidden',
      border: `1px solid ${cardBorder}`, height: '100%', minHeight: 480,
    }}>
      <div style={{
        background: `linear-gradient(135deg, ${d.accent}30, ${d.bg})`,
        padding: '20px 24px', textAlign: 'center',
        borderBottom: `1px solid ${cardBorder}`,
      }}>
        <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: 2, color: d.accent, marginBottom: 4, textTransform: 'uppercase' }}>
          Point of Sale
        </div>
        <div style={{ fontFamily: d.headerFont, fontSize: 22, fontWeight: 700, color: textColor }}>
          {d.businessName}
        </div>
      </div>

      <div style={{ padding: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: textColor, marginBottom: 10 }}>Products</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
          {d.inventory.slice(0, 6).map((item, i) => (
            <div
              key={i}
              onClick={() => addToCart(item)}
              style={{
                padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                background: cardBg, border: `1px solid ${cardBorder}`,
                transition: 'all 0.2s',
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 600, color: textColor, marginBottom: 2 }}>{item.name}</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: d.accent }}>${item.price}</span>
                <span style={{ fontSize: 10, color: textMuted }}>{item.stock} in stock</span>
              </div>
            </div>
          ))}
        </div>

        {/* Cart */}
        <div style={{
          background: cardBg, borderRadius: 10, padding: 14,
          border: `1px solid ${cardBorder}`,
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: textColor, marginBottom: 8 }}>
            🛒 Cart {cart.length > 0 && `(${cart.reduce((s, c) => s + c.qty, 0)} items)`}
          </div>
          {cart.length === 0 ? (
            <div style={{ fontSize: 12, color: textMuted, textAlign: 'center', padding: '12px 0' }}>
              Tap a product to add it
            </div>
          ) : (
            <>
              {cart.map((item, i) => (
                <div key={i} style={{
                  display: 'flex', justifyContent: 'space-between', padding: '6px 0',
                  borderBottom: `1px solid ${cardBorder}`, fontSize: 12, color: textColor,
                }}>
                  <span>{item.name} × {item.qty}</span>
                  <span style={{ fontWeight: 700 }}>${(item.price * item.qty).toFixed(2)}</span>
                </div>
              ))}
              <div style={{
                display: 'flex', justifyContent: 'space-between', padding: '10px 0 0',
                marginTop: 6, fontSize: 15, fontWeight: 700, color: d.accent,
              }}>
                <span>Total</span>
                <span>${total.toFixed(2)}</span>
              </div>
              <button
                onClick={() => setCart([])}
                style={{
                  width: '100%', padding: '10px 0', borderRadius: 8, border: 'none',
                  background: d.accent, color: '#fff', fontSize: 13, fontWeight: 700,
                  cursor: 'pointer', marginTop: 10,
                }}
              >
                Complete Sale
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// RENTAL ORDER PREVIEW
// ============================================================================
function RentalOrderPreview({ industry }) {
  const d = INDUSTRIES[industry];
  const [selectedItem, setSelectedItem] = useState(null);
  const [days, setDays] = useState(3);
  const textColor = '#fff';
  const textMuted = 'rgba(255,255,255,0.5)';
  const cardBg = 'rgba(255,255,255,0.06)';
  const cardBorder = 'rgba(255,255,255,0.08)';
  const accent = d.accent;

  const selected = selectedItem !== null ? d.equipment[selectedItem] : null;
  const subtotal = selected ? selected.priceDay * days : 0;
  const deposit = selected ? selected.deposit : 0;
  const total = subtotal + deposit;

  return (
    <div style={{
      background: d.bg, borderRadius: 16, overflow: 'hidden',
      border: `1px solid ${cardBorder}`, height: '100%', minHeight: 480,
    }}>
      {/* Header */}
      <div style={{
        background: `linear-gradient(135deg, ${accent}30, ${d.bg})`,
        padding: '20px 24px', textAlign: 'center',
        borderBottom: `1px solid ${cardBorder}`,
      }}>
        <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: 2, color: accent, marginBottom: 4, textTransform: 'uppercase' }}>
          Request a Rental
        </div>
        <div style={{ fontFamily: d.headerFont, fontSize: 22, fontWeight: 700, color: textColor }}>
          {d.businessName}
        </div>
        <div style={{ fontSize: 12, color: textMuted, marginTop: 2 }}>{d.tagline}</div>
      </div>

      <div style={{ padding: '16px 20px', overflowY: 'auto', maxHeight: 420 }}>
        {/* Equipment grid */}
        {!selected ? (
          <>
            <div style={{ fontSize: 12, fontWeight: 600, color: textMuted, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>
              Available Equipment
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {d.equipment.map((item, i) => (
                <div key={i} onClick={() => setSelectedItem(i)} style={{
                  background: cardBg, border: `1px solid ${cardBorder}`,
                  borderRadius: 10, padding: '12px', cursor: 'pointer',
                  transition: 'all 0.2s',
                }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: textColor, marginBottom: 4 }}>{item.name}</div>
                  <div style={{ fontSize: 11, color: accent, fontWeight: 600 }}>${item.priceDay}/day</div>
                  <div style={{ fontSize: 11, color: textMuted, marginTop: 2 }}>{item.available} available</div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            <button onClick={() => { setSelectedItem(null); setDays(3); }} style={{
              background: 'none', border: 'none', color: accent, fontSize: 13,
              cursor: 'pointer', marginBottom: 12, padding: 0, fontWeight: 600,
            }}>← Back to equipment</button>

            <div style={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 12, padding: 16, marginBottom: 12 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: textColor, marginBottom: 4 }}>{selected.name}</div>
              <div style={{ fontSize: 13, color: textMuted, marginBottom: 12 }}>{selected.category}</div>

              <div style={{ fontSize: 12, color: textMuted, marginBottom: 6 }}>Rental Duration</div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                {[1, 3, 5, 7].map(d => (
                  <button key={d} onClick={() => setDays(d)} style={{
                    flex: 1, padding: '8px 0', borderRadius: 8, border: 'none',
                    background: days === d ? accent : 'rgba(255,255,255,0.08)',
                    color: days === d ? '#fff' : textMuted,
                    fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  }}>{d}d</button>
                ))}
              </div>

              <div style={{ borderTop: `1px solid ${cardBorder}`, paddingTop: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontSize: 13, color: textMuted }}>Rental ({days} days × ${selected.priceDay})</span>
                  <span style={{ fontSize: 13, color: textColor, fontWeight: 600 }}>${subtotal.toLocaleString()}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                  <span style={{ fontSize: 13, color: textMuted }}>Refundable Deposit</span>
                  <span style={{ fontSize: 13, color: textColor, fontWeight: 600 }}>${deposit.toLocaleString()}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: `1px solid ${cardBorder}`, paddingTop: 10 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: textColor }}>Due Today</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: accent }}>${total.toLocaleString()}</span>
                </div>
              </div>
            </div>

            <button style={{
              width: '100%', padding: '14px', borderRadius: 10, border: 'none',
              background: `linear-gradient(135deg, ${accent}, ${accent}cc)`,
              color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer',
            }}>
              Request This Equipment →
            </button>
          </>
        )}
      </div>
    </div>
  );
}
// ============================================================================
// MAIN DEMO PAGE
// ============================================================================
export default function DemoPage() {
  const [activeIndustry, setActiveIndustry] = useState('spa');
  const d = INDUSTRIES[activeIndustry];

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0c0c14',
      fontFamily: "'DM Sans', -apple-system, sans-serif",
    }}>
      {/* Google Fonts */}
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600;700&family=Jost:wght@300;400;500;600&family=Bebas+Neue&family=Inter:wght@400;500;600;700&family=Oswald:wght@400;500;600;700&family=Space+Grotesk:wght@400;500;600;700&family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />

      {/* DEMO BANNER */}
      <div style={{
        background: 'linear-gradient(90deg, #7C3AED, #2563EB)',
        padding: '10px 20px',
        textAlign: 'center',
        fontSize: 13,
        fontWeight: 600,
        color: '#fff',
        letterSpacing: 0.3,
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}>
        🎯 INTERACTIVE DEMO — This is a sandboxed preview with sample data. No real bookings are created.
        <a
          href="/app"
          style={{
            color: '#fff', marginLeft: 16, textDecoration: 'underline',
            fontWeight: 700, opacity: 0.9,
          }}
        >
          Sign Up Free →
        </a>
      </div>

      {/* Hero */}
      <div style={{
        padding: '48px 24px 24px', textAlign: 'center',
        maxWidth: 800, margin: '0 auto',
      }}>
        <div style={{
          fontSize: 11, fontWeight: 700, letterSpacing: 3, color: '#7C3AED',
          textTransform: 'uppercase', marginBottom: 12,
        }}>
          SEE IT IN ACTION
        </div>
        <h1 style={{
          fontSize: 'clamp(28px, 5vw, 44px)', fontWeight: 800, color: '#fff',
          lineHeight: 1.15, margin: '0 0 12px',
          background: 'linear-gradient(135deg, #fff 0%, rgba(255,255,255,0.7) 100%)',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
        }}>
          One Platform. Every Industry.
        </h1>
        <p style={{
          fontSize: 16, color: 'rgba(255,255,255,0.5)', maxWidth: 520,
          margin: '0 auto 32px', lineHeight: 1.6,
        }}>
          Explore both sides — the <strong style={{ color: 'rgba(255,255,255,0.8)' }}>owner dashboard</strong> your
          business runs on, and the <strong style={{ color: 'rgba(255,255,255,0.8)' }}>booking page</strong> your
          customers see.
        </p>
      </div>

      {/* Industry Tabs */}
      <div style={{
        display: 'flex', justifyContent: 'center', gap: 8, padding: '0 20px 32px',
        flexWrap: 'wrap',
      }}>
        {Object.values(INDUSTRIES).map(ind => (
          <button
            key={ind.key}
            onClick={() => setActiveIndustry(ind.key)}
            style={{
              padding: '10px 20px', borderRadius: 10, border: 'none',
              background: activeIndustry === ind.key
                ? ind.accent
                : 'rgba(255,255,255,0.06)',
              color: activeIndustry === ind.key ? '#fff' : 'rgba(255,255,255,0.5)',
              fontSize: 14, fontWeight: 600, cursor: 'pointer',
              transition: 'all 0.3s',
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            <span>{ind.icon}</span> {ind.label}
          </button>
        ))}
      </div>

      {/* Labels */}
      <div style={{
        maxWidth: 1100, margin: '0 auto', padding: '0 20px 8px',
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20,
      }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.4)', letterSpacing: 1, textTransform: 'uppercase' }}>
          👤 What {d.businessName.split(' ')[0]} sees (Owner Dashboard)
        </div>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.4)', letterSpacing: 1, textTransform: 'uppercase' }}>
          📱 What customers see ({activeIndustry === 'smoke' ? 'Point of Sale' : 'Booking Page'})
        </div>
      </div>

      {/* Side by Side Previews */}
      <div style={{
        maxWidth: 1100, margin: '0 auto', padding: '0 20px 48px',
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20,
      }}>
        <DashboardPreview industry={activeIndustry} />
        <BookingPreview industry={activeIndustry} />
      </div>

      {/* Feature Callouts */}
      <div style={{
        maxWidth: 1100, margin: '0 auto', padding: '0 20px 48px',
      }}>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16,
        }}>
          {[
            { icon: '📅', title: 'Online Booking', desc: 'Customers book 24/7 from any device' },
            { icon: '💬', title: 'SMS Campaigns', desc: 'Text your customers promos & reminders' },
            { icon: '👥', title: 'Built-in CRM', desc: 'Track visits, spending, and VIP status' },
            { icon: '📊', title: 'Analytics', desc: 'Revenue, bookings, and growth trends' },
            { icon: '🌐', title: 'Free Website', desc: 'Custom branded site included' },
            { icon: '💳', title: 'Invoicing & POS', desc: 'Collect payments in-person or online' },
          ].map((f, i) => (
            <div key={i} style={{
              background: 'rgba(255,255,255,0.04)', borderRadius: 12, padding: '20px 18px',
              border: '1px solid rgba(255,255,255,0.06)',
            }}>
              <div style={{ fontSize: 24, marginBottom: 8 }}>{f.icon}</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 4 }}>{f.title}</div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)', lineHeight: 1.5 }}>{f.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div style={{
        textAlign: 'center', padding: '32px 20px 64px',
      }}>
        <div style={{
          fontSize: 28, fontWeight: 800, color: '#fff', marginBottom: 8,
        }}>
          Ready to ditch the expensive software?
        </div>
        <div style={{ fontSize: 15, color: 'rgba(255,255,255,0.5)', marginBottom: 24 }}>
          Starting at $99/mo — a fraction of what Mindbody and Vagaro charge.
        </div>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          <a
            href="/#pricing"
            style={{
              padding: '14px 32px', borderRadius: 10, border: 'none',
              background: 'linear-gradient(135deg, #7C3AED, #2563EB)',
              color: '#fff', fontSize: 15, fontWeight: 700, textDecoration: 'none',
              cursor: 'pointer',
            }}
          >
            View Pricing
          </a>
          <a
            href="/#demo-form"
            style={{
              padding: '14px 32px', borderRadius: 10,
              border: '1px solid rgba(255,255,255,0.2)',
              background: 'transparent',
              color: '#fff', fontSize: 15, fontWeight: 700, textDecoration: 'none',
              cursor: 'pointer',
            }}
          >
            Request a Demo
          </a>
        </div>
      </div>

      {/* Responsive */}
      <style>{`
        @media (max-width: 768px) {
          div[style*="gridTemplateColumns: '1fr 1fr'"],
          div[style*="grid-template-columns: 1fr 1fr"] {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}
